import React, { useEffect, useRef, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck, FileDown, Loader2, PenLine, AlertTriangle } from "lucide-react";
import { SignatureCanvas } from "@/components/orders/SignaturePadDialog";

const fmtDt = (v) => (v ? new Date(v).toLocaleString("de-CH", { dateStyle: "medium", timeStyle: "short" }) : "–");

export default function DpaCard({ companyId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => { if (companyId) apiClient.get(`/companies/${companyId}/dpa`).then((r) => setData(r.data)).catch(() => setData(null)); };
  useEffect(load, [companyId]);

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const r = await apiClient.get(`/companies/${companyId}/dpa/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `AVV_v${data?.version || ""}${data?.accepted ? "_unterzeichnet" : ""}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      toast.success("AVV-PDF heruntergeladen");
    } catch { toast.error("PDF konnte nicht erzeugt werden"); } finally { setBusy(false); }
  };

  if (!data) return <div data-testid="dpa-card" className="text-xs text-slate-400">AVV wird geladen …</div>;
  const acc = data.acceptance;
  return (
    <div data-testid="dpa-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h2 className="text-sm font-medium text-slate-800 mb-1 flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" />Datenschutz – Auftragsverarbeitungsvertrag (AVV)</h2>
          <p className="text-xs text-slate-500">
            Vertrag nach Art. 9 DSG und Art. 28 DSGVO zwischen Ihrer Firma (Verantwortlicher) und {data.processor?.name} (Auftragsverarbeiter)
            – inkl. technischer und organisatorischer Massnahmen und der genehmigten Unterauftragsverarbeiter. Version {data.version}.
          </p>
          <div className="mt-2">
            {data.accepted ? (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs text-emerald-800" data-testid="dpa-status-accepted">
                <ShieldCheck className="h-3.5 w-3.5" />Angenommen am {fmtDt(acc?.accepted_at)} durch {acc?.signer_name}{acc?.signer_role ? ` (${acc.signer_role})` : ""}{acc?.has_signature ? " · unterschrieben" : ""}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs text-amber-800" data-testid="dpa-status-open">
                <AlertTriangle className="h-3.5 w-3.5" />{data.outdated_acceptance ? `Neue Version ${data.version} – bisherige Annahme (v${data.outdated_acceptance.version}) muss erneuert werden` : "Noch nicht angenommen"}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button type="button" size="sm" variant="outline" onClick={downloadPdf} disabled={busy} data-testid="dpa-pdf-button">
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <FileDown className="h-3.5 w-3.5 mr-1" />}AVV als PDF
          </Button>
          {!data.accepted && (
            <Button type="button" size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={() => setOpen(true)} data-testid="dpa-accept-open-button">
              <PenLine className="h-3.5 w-3.5 mr-1" />AVV annehmen
            </Button>
          )}
        </div>
      </div>
      <details className="mt-3 text-xs text-slate-600" data-testid="dpa-subprocessors">
        <summary className="cursor-pointer text-slate-700">Unterauftragsverarbeiter ({data.subprocessors?.length || 0})</summary>
        <ul className="mt-1.5 space-y-0.5 pl-4 list-disc">
          {(data.subprocessors || []).map((s) => <li key={s.name}><span className="font-medium">{s.name}</span> – {s.purpose} <span className="text-slate-400">({s.location})</span></li>)}
        </ul>
      </details>
      <DpaAcceptDialog open={open} onOpenChange={setOpen} companyId={companyId} data={data} onDone={load} />
    </div>
  );
}

function DpaAcceptDialog({ open, onOpenChange, companyId, data, onDone }) {
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => { if (open) { setName(""); setRole(""); setConfirm(false); setHasInk(false); } }, [open]);

  const submit = async () => {
    if (!confirm) { toast.error("Bitte bestätigen Sie die Kenntnisnahme und Zeichnungsberechtigung"); return; }
    if (name.trim().length < 3) { toast.error("Bitte Namen der zeichnungsberechtigten Person eingeben"); return; }
    setBusy(true);
    try {
      const canvas = wrapRef.current?.querySelector("canvas");
      await apiClient.post(`/companies/${companyId}/dpa/accept`, { signer_name: name.trim(), signer_role: role.trim() || null, confirm: true, signature_png: hasInk && canvas ? canvas.toDataURL("image/png") : null });
      toast.success(`AVV Version ${data.version} angenommen`);
      onOpenChange(false);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Annahme fehlgeschlagen"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dpa-accept-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" />AVV Version {data.version} annehmen</DialogTitle></DialogHeader>
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 max-h-56 overflow-y-auto text-xs text-slate-700 space-y-2" data-testid="dpa-contract-text">
          {(data.sections || []).map((s) => (
            <div key={s.title}><div className="font-medium text-slate-900">{s.title}</div>{s.paragraphs.map((p, i) => <p key={i} className="mt-0.5">{p}</p>)}</div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1"><Label htmlFor="dpa-name">Name (zeichnungsberechtigt)</Label><Input id="dpa-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Vorname Nachname" data-testid="dpa-signer-name-input" /></div>
          <div className="space-y-1"><Label htmlFor="dpa-role">Funktion</Label><Input id="dpa-role" value={role} onChange={(e) => setRole(e.target.value)} placeholder="z. B. Geschäftsführer/in" data-testid="dpa-signer-role-input" /></div>
        </div>
        <div className="space-y-1">
          <Label>Unterschrift (optional)</Label>
          <div ref={wrapRef}>{open && <SignatureCanvas onChange={setHasInk} />}</div>
        </div>
        <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer" data-testid="dpa-confirm-label">
          <Checkbox checked={confirm} onCheckedChange={(v) => setConfirm(!!v)} data-testid="dpa-confirm-checkbox" className="mt-0.5" />
          <span>Ich habe den Auftragsverarbeitungsvertrag inkl. Anhang A (TOM) und Anhang B (Unterauftragsverarbeiter) gelesen und bin für die Firma zeichnungsberechtigt. Zeitpunkt, Benutzerkonto und IP-Adresse werden als Nachweis gespeichert.</span>
        </label>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="dpa-cancel-button">Abbrechen</Button>
          <Button type="button" onClick={submit} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="dpa-accept-submit-button">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1" />}Verbindlich annehmen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
